import { Link } from 'react-router-dom';
import type { LineScoreState } from '../state/selectors';
import { teamColor } from '../state/teamColors';
import styles from './LineScore.module.css';

interface LineScoreProps {
  state: LineScoreState;
  awayTeamCode: string;
  homeTeamCode: string;
  /** Open the inning-detail modal at the clicked half-inning. */
  onInningClick?: (inning: number, half: 'top' | 'bottom') => void;
  /**
   * When set, render a "Box score →" link in the heading that deep-links to
   * `/games/:gamePk/boxscore`. Omit on the box-score page itself (self-link).
   */
  gamePk?: number;
  /** Currently-active inning number; the matching half-cell gets a highlight outline. */
  currentInning?: number | null;
  /** Currently-active half ('top' = away batting, 'bottom' = home). */
  currentHalf?: 'top' | 'bottom' | null;
  /** Live → red/yellow highlight; historical → blue. Defaults to historical. */
  mode?: 'live' | 'historical';
}

export default function LineScore({
  state,
  awayTeamCode,
  homeTeamCode,
  onInningClick,
  gamePk,
  currentInning = null,
  currentHalf = null,
  mode = 'historical',
}: LineScoreProps): JSX.Element {
  const innings =
    state.innings.length > 0
      ? state.innings
      : Array.from({ length: 9 }, (_, i) => ({ inning: i + 1, away: null, home: null }));
  const away = teamColor(awayTeamCode);
  const home = teamColor(homeTeamCode);

  function renderInningCell(
    inning: number,
    half: 'top' | 'bottom',
    value: number | null,
    testidPrefix: string,
  ): JSX.Element {
    const display = value ?? '';
    const isCurrent = currentInning != null && currentHalf === half && inning === currentInning;
    const currentClass = isCurrent
      ? ` ${mode === 'live' ? styles.cellCurrentLive : styles.cellCurrent}`
      : '';
    if (!onInningClick) {
      return (
        <td
          key={inning}
          className={`${styles.cell}${currentClass}`}
          data-testid={`${testidPrefix}-${inning}`}
          data-current={isCurrent ? 'true' : undefined}
        >
          {display}
        </td>
      );
    }
    return (
      <td
        key={inning}
        className={`${styles.cell}${currentClass}`}
        data-testid={`${testidPrefix}-${inning}`}
        data-current={isCurrent ? 'true' : undefined}
      >
        <button
          type="button"
          className={styles.cellButton}
          onClick={() => onInningClick(inning, half)}
          aria-label={`${half === 'top' ? 'Top' : 'Bottom'} of inning ${inning} — ${value ?? 'no runs'}`}
        >
          {display === '' ? ' ' : display}
        </button>
      </td>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.headingRow}>
        <h2 className={styles.heading}>Line score</h2>
        {gamePk != null && (
          <Link
            to={`/games/${gamePk}/boxscore`}
            className={styles.boxscoreLink}
            data-testid="line-score-boxscore-link"
          >
            Box score →
          </Link>
        )}
      </div>
      <table data-testid="line-score" className={styles.table}>
        <thead>
          <tr>
            <th className={styles.headCell}></th>
            {innings.map((i) => (
              <th key={i.inning} className={styles.headCell} data-testid={`line-score-head-${i.inning}`}>
                {i.inning}
              </th>
            ))}
            <th className={`${styles.headCell} ${styles.totalsCell}`}>R</th>
            <th className={styles.headCell}>H</th>
            <th className={styles.headCell}>E</th>
          </tr>
        </thead>
        <tbody>
          <tr data-testid="line-score-row-away">
            <th scope="row" className={styles.teamHead} style={{ background: away.primary }}>{state.away.abbr}</th>
            {innings.map((i) => renderInningCell(i.inning, 'top', i.away, 'line-score-away'))}
            <td className={`${styles.cell} ${styles.totalsCell}`}>{state.away.runs}</td>
            <td className={`${styles.cell} ${styles.smallTotal}`}>{state.away.hits}</td>
            <td className={`${styles.cell} ${styles.smallTotal}`}>{state.away.errors}</td>
          </tr>
          <tr data-testid="line-score-row-home">
            <th scope="row" className={styles.teamHead} style={{ background: home.primary }}>{state.home.abbr}</th>
            {innings.map((i) => renderInningCell(i.inning, 'bottom', i.home, 'line-score-home'))}
            <td className={`${styles.cell} ${styles.totalsCell}`}>{state.home.runs}</td>
            <td className={`${styles.cell} ${styles.smallTotal}`}>{state.home.hits}</td>
            <td className={`${styles.cell} ${styles.smallTotal}`}>{state.home.errors}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
